<script lang="ts" setup>
import type { Brand, BrandThemePalette } from "~/services/BrandService";
import Color from "~/utils/color";
import { faCheck, faText } from "@fortawesome/pro-solid-svg-icons";

const props = withDefaults(
    defineProps<{
        brand: Brand;
        small?: boolean;
        copyable?: boolean;
        copyMode: "color" | "variable";
        copyPrefix: string;
        fallback?: BrandThemePalette;
    }>(),
    {
        copyMode: "color",
        copyPrefix: "brand.",
    },
);

const palette = computed(() =>
    [
        {
            label: "Primary",
            key: "primary",
            color:
                props.brand?.theme?.palette?.primary?.color ??
                props.fallback?.primary?.color ??
                "#000000",
            text_color:
                props.brand?.theme?.palette?.primary?.text_color ??
                props.fallback?.primary?.text_color ??
                "#FFFFFF",
        },
        {
            label: "Secondary",
            key: "secondary",
            color:
                props.brand?.theme?.palette?.secondary?.color ??
                props.fallback?.secondary?.color ??
                "#000000",
            text_color:
                props.brand?.theme?.palette?.secondary?.text_color ??
                props.fallback?.secondary?.text_color ??
                "#FFFFFF",
        },
        {
            label: "Accent",
            key: "accent",
            color:
                props.brand?.theme?.palette?.accent?.color ??
                props.fallback?.accent?.color ??
                "#000000",
            text_color:
                props.brand?.theme?.palette?.accent?.text_color ??
                props.fallback?.accent?.text_color ??
                "#FFFFFF",
        },
        {
            label: "Background",
            key: "background",
            color:
                props.brand?.theme?.palette?.background?.color ??
                props.fallback?.background?.color ??
                "#FFFFFF",
            text_color:
                props.brand?.theme?.palette?.background?.text_color ??
                props.fallback?.background?.text_color ??
                "#000000",
        },
        {
            label: "Surface",
            key: "surface",
            color:
                props.brand?.theme?.palette?.surface?.color ??
                props.fallback?.surface?.color ??
                "#FFFFFF",
            text_color:
                props.brand?.theme?.palette?.surface?.text_color ??
                props.fallback?.surface?.text_color ??
                "#000000",
        },
    ].map((colors) => {
        let color = new Color(colors.color);
        let text_color = new Color(colors.text_color);

        return {
            ...colors,
            color_dark: color.isDark(-20),
            text_dark: color.isDark(-20),
        };
    }),
);

function variable(type: string, key: string) {
    return `{{${props.copyPrefix}theme.palette.${type}.${key}}}`;
}
</script>

<template>
    <div class="relative rounded-lg grid grid-cols-5 overflow-hidden">
        <div
            v-for="color in palette"
            :key="color?.color ?? ''"
            :style="{
                background: color?.color,
                boxShadow: `inset -3px -3px ${color.color_dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
            }"
            class="relative flex col-span-1 h-full p-2 first-of-type:rounded-l-lg last-of-type:rounded-r-lg"
        >
            <FontAwesomeIcon
                :icon="faText"
                :size="small ? 'sm' : 'xl'"
                :style="{
                    color: color?.text_color,
                    filter: `drop-shadow(1.5px 1.5px ${color.color_dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'})`,
                }"
                class="self-end"
            />
            <span v-if="!small" class="absolute w-full left-0 px-2">
                <svg
                    :fill="color?.text_color"
                    class="w-[160%]"
                    viewBox="0 0 500 500"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <text
                        :font-size="500 / color.label.length"
                        class="font-display"
                        lengthAdjust="spacingAndGlyphs"
                        x="0"
                        y="75"
                    >
                        {{ color.label }}
                    </text>
                </svg>
            </span>
            <div
                v-if="copyable"
                class="absolute w-full h-full opacity-0 hover:opacity-100 hover:bg-black/10 transition duration-150 left-0 top-0 grid grid-rows-2"
            >
                <UICopy
                    v-slot="{ copy, copied }"
                    :source="
                        copyMode === 'color'
                            ? color.text_color
                            : variable(color.key, 'color')
                    "
                >
                    <div
                        :style="{
                            background: color.color,
                            color: color.text_color,
                        }"
                        class="flex items-center select-none justify-center text-xs"
                        @click.stop="copy()"
                    >
                        <FontAwesomeIcon v-if="copied" :icon="faCheck" />
                        <span v-else>{{ color.color }}</span>
                    </div>
                </UICopy>
                <UICopy
                    v-slot="{ copy, copied }"
                    :source="
                        copyMode === 'color'
                            ? color.text_color
                            : variable(color.key, 'text_color')
                    "
                >
                    <div
                        :style="{
                            color: color.color,
                            background: color.text_color,
                        }"
                        class="flex items-center select-none justify-center text-xs"
                        @click.stop="copy()"
                    >
                        <FontAwesomeIcon v-if="copied" :icon="faCheck" />
                        <span v-else>{{ color.text_color }}</span>
                    </div>
                </UICopy>
            </div>
        </div>
    </div>
</template>
